import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subject } from 'rxjs';
import { filterPageTypes } from 'src/app/store/filters.era';
import { allDefaultPublishColumns, PublishDotMenuChoices, publishTableColumnsById } from 'src/app/helpers/table-helpers';
import { publishReselector, publishInfoReselector, brandDataReselector } from 'src/app/store/reselectors/publish.reselector';
import { PublishInfo } from 'src/app/store/events.era';
import { JoiEvent } from '../../store/events.era';
import { AllSameKeysToTypeEnforcer } from '../../helpers/helpers';
import { ConfirmDeleteData, openConfirmDeleteGetResult } from 'src/app/components/dialog.confirmDelete.component';
import { accountEventStates } from 'src/app/store/reselectors/null.reselector';
import { SimpleConfirmationDialogComponent, ConfirmationDialogData } from 'src/app/components/dialogues-general/simple-confirmation.dialog.component';
import { TableColumn, TableMenuItem, TableDotMenuItem } from 'src/app/helpers/table.data';
import { PublishTableData, publishTableColumns } from 'src/app/helpers/table-column-types';
import { JoiFile } from 'globals/store/files.model';
import { LinksDialogData, LinksDialogComponent } from './../../components/dialogues-general/links.dialog.component';
import { eventListIdsByPublishIdsReselector } from 'src/app/store/reselectors/event-lists.reselector';
import { Wrapper } from 'src/app/components/wrapper.component';
import { BrandingData, BrandingSelectData } from 'globals/store/publish.data';
import { BlackBarWrapperComponent } from 'src/app/components/wrappers/black-bar-wrapper.component';
import { ProgramPublishViewComponent } from './program.publish.view.component';
import { CommonModule, DatePipe } from '@angular/common';
import { LoaderPaneFragmentComponent } from 'src/app/components/loader/loader-pane-fragment.component';
import { NestedTableComponent } from 'src/app/components/nested-table/nested-table.component';
import { CommandMenuComponent } from 'src/app/components/nav-bar/command-menu.component';
import { TopMenuComponent } from './top.menu';
import { MobileMenuComponent } from 'src/app/components/menu/mobile-menu.component';
import { JoiMessageComponent } from 'src/app/components/cards/joi-message.component';
import { traceNames } from 'src/app/services/performance.tracking.data';

@Component({
    selector: 'program-publish',
    template: `
<top-menu></top-menu>
<mobile-menu></mobile-menu>
<div style="padding: 0 0 13px 0; ">
  <command-menu *ngIf="!selectedPublishInfo"
    [filterPage]="filterPage"
    [canEditColumns]="false"
    [addItemText]="!event || event.is_readonly ? '' : '+ CREATE PAGE'"
    (addItem)="addTimeline()"
  ></command-menu>
</div>
<joi-message *ngIf="message" [message]="message"></joi-message>
<nested-table
  *ngIf="!selectedPublishInfo"
  [filtersId]="filterPage"
  [items]="publishList"
  [columns]="columnData"
  [columnsById]="columnsById"
  [bigSecondColumn]="true"
  [readOnly]="!event || event.is_readonly"
  [menuItems]="menuItems"
  [dotMenuItems]="dotMenuItems"
  [showEmptyDiv]="!publishList.length && loadedTableDataOnce"
  [defaultSelectedColumnId]="defaultColumnId"
  (selectItem)="openPublishViewFromTable($event)"
  (updatedSelectedIds)="selectIds($event)"
>
  <div class="empty-table">
  <loader-pane-fragment [trace]="traceNames.viewProgramPublish"></loader-pane-fragment>
    <div class="text-wrapper" *ngIf="!rowsBeforeFiltering && event?.closed">
      <h3>Event closed</h3>
      This event is closed. Only active events can be published.
    </div>
    <div class="text-wrapper" *ngIf="!rowsBeforeFiltering">
      <h3>Publish a page</h3>
      Here you can publish your program page to a shareable URL. You can even create multiple published pages with different filters for different audiences!
    </div>
    <div class="text-wrapper" *ngIf="!!rowsBeforeFiltering">
      <h3>No results</h3>
      There are no published pages that match your search query.
    </div>
  </div>
</nested-table>
<black-bar-wrapper
  *ngIf="selectedPublishInfo && brandingData"
  (close)="closePublishedItem()"
><div class="header">{{ selectedPublishInfo.title || event.title }}</div>
  <program-publish-view
    *ngIf="event && selectedPublishInfo"
    [startMobile]="startMobile"
    [publishInfo]="selectedPublishInfo"
    [brandingData]="brandingData"
    [editPublishView]="editCurrentPublishView.bind(this)"
    [editCTALinks]="editCurrentPublishViewCTALinks.bind(this)"
    [event]="event"
  ></program-publish-view>
</black-bar-wrapper>
  `,
    styleUrls: [`./program.publish.scss`, '../../components/nested-table/nested-table.scss', '../../page.scss'],
    standalone: true,
    imports: [BlackBarWrapperComponent, ProgramPublishViewComponent, CommonModule, LoaderPaneFragmentComponent, NestedTableComponent, CommandMenuComponent, TopMenuComponent, MobileMenuComponent, JoiMessageComponent],
    providers: [DatePipe],
})
export class ProgramPublishComponent extends Wrapper<ProgramPublishComponent> implements OnInit, OnDestroy {

  readonly _file: string = "program.publish.component.ts";
  private $selectTimeline: Subject<boolean> = new Subject()
  filterPage = filterPageTypes.publish
  nullState = accountEventStates.notPublished
  nullTitle = 'Publish a page'
  selectedPublishInfo: PublishInfo
  traceNames = traceNames
  publishCode: string
  loaded = false
  message = ''
  oneWillUpdateMessage = "1 page will be updated immediately."
  oneUpdatedMessage = "1 page was updated successfully"
  searchTerm = ''
  loadedTableDataOnce = false
  rowsBeforeFiltering = 0
  startMobile = false

  canUpdateCount = 0
  MESSAGE_DISPLAY_MS = 5000
  selectedRowIds: Set<string> = new Set()

  event: JoiEvent
  columnData: TableColumn[] = allDefaultPublishColumns
  columnsById: { [id: string]: TableColumn } = publishTableColumnsById
  singleSelectionMenuItems: TableMenuItem[] = [
    { title: 'Edit', method: this.editPublishView.bind(this) },
    { title: 'Duplicate', method: this.duplicatePublishView.bind(this) },
  ]
  multiSelectionMenuItems: TableMenuItem[] = [
    { title: 'Delete', method: this.deletePublishView.bind(this) }
  ]
  menuItems: TableMenuItem[] = []
  dotMenuItems: AllSameKeysToTypeEnforcer<PublishDotMenuChoices, TableDotMenuItem> = {
    update: { id: 'update', title: 'Update', method: this.updateSinglePublishView.bind(this) },
    view: { id: 'view', title: 'View', method: this.openPublishViewFromTable.bind(this) },
    edit: { id: 'edit', title: 'Edit', method: this.editSinglePublishView.bind(this) },
    delete: { id: 'delete', title: 'Delete', method: this.deleteSinglePublishView.bind(this) },
    duplicate: { id: 'duplicate', title: 'Duplicate', method: this.duplicateSinglePublishView.bind(this) },
  }


  defaultColumnId = publishTableColumns.name
  publishList: PublishTableData[] = []
  publishTableRowsById: { [id:string]: PublishTableData } = {}
  eventListIdsByPublishIds: { [id:string]: Set<string> } = {}
  brandingData: BrandingData

  constructor() {
    super()
    this.uiService.changePageTitle("Publish View", true)
  }

  ngOnInit() {
    this.subProp(this.store.select('filters', 'publish', 'search'), 'searchTerm')
    this.subProp(this.store.select('events', 'event'), 'event')
    this.subCallback(this.store.select(publishReselector), ({ publishTableData, publishTableRowsById, publishCountBeforeFiltering }) => {
      this.publishList = publishTableData
      this.publishTableRowsById = publishTableRowsById
      this.rowsBeforeFiltering = publishCountBeforeFiltering
      this.loadedTableDataOnce = true
    })
    this.subProp(this.store.select(eventListIdsByPublishIdsReselector), 'eventListIdsByPublishIds')
  }

  selectIds(ids: Set<string>) {
    setTimeout(() => {
      
      this.canUpdateCount = Array.from(ids).reduce((tot, id) => tot + (this.publishTableRowsById[id].publishInfo.status === 'published' ? 1 : 0), 0)
      this.menuItems = ids.size === 1 ? [...this.singleSelectionMenuItems, ...this.multiSelectionMenuItems] : [...this.multiSelectionMenuItems]
      if (this.canUpdateCount === ids.size) this.menuItems.unshift({ title: 'Update', method: this.updatePublishViews.bind(this) })
    }, 0)
  }

  openPublishViewFromTable(publishItem: PublishTableData) {
    this.openPublishView(publishItem.publishInfo.publishCode)
  }

  openPublishView(publishCode: string) {
    console.log({ publishCode })
    this.startMobile = window.innerWidth <= 768
    this.publishCode = publishCode
    this.subProp(this.store.select(publishInfoReselector, publishCode), 'selectedPublishInfo', this.$selectTimeline)
    const brandingSelectData: BrandingSelectData = {
      type: 'publish',
      publishId: publishCode
    }
    this.selectProp(brandDataReselector, brandingSelectData, 'brandingData', this.$selectTimeline)
  }

  closePublishedItem() {
    this.brandingData = null
    this.publishCode = null;
    this.selectedPublishInfo = null;
    this.$selectTimeline.next(true)
  }

  editCurrentPublishView() { // pass this into program publish view instead of attaching all of that logic there
    // console.log(this.selectedPublishInfo)
    this.editSinglePublishView({ publishInfo: this.selectedPublishInfo })
  }

  editCurrentPublishViewCTALinks() {
    this.editPublishViewCTA({ publishInfo: this.selectedPublishInfo })
  }

  async editPublishViewCTA({ publishInfo }: Partial<PublishTableData>) {
    const data: LinksDialogData = {
      links: publishInfo.CTALinks,
      dialogTitle: 'CTA Links',
      linkType: 'CTA',
      description: 'Add call-to-action links to your inner pages',
      linksTitle: 'CTA Links',
      linksEmptyText: 'No CTA links',
      addLinkText: 'ADD A CTA LINK',
      namePlaceholder: 'CTA text',
    }
    
    const CTALinks: JoiFile[] = await this.openDialogGetResult<JoiFile[]>(LinksDialogComponent, { width: '480px', disableClose: true, data })
    console.log({ CTALinks })
    if (!CTALinks) return // returns null for cancel or no changes, [] if removed all so this won't hit
    this.sharePublishService.changePublishViewCTALinks(publishInfo, CTALinks)

  }


  editSinglePublishView({ publishInfo }: Partial<PublishTableData>) {
    this.openGreenInputDialog('Page Name and Description', 'Update your page name and description', 'Page name', 'UPDATE', publishInfo.title, false, publishInfo.description, 'Page description').then(result => result && this.sharePublishService.changePublishViewNameDescription(publishInfo, result.values[0], result.values[1]))
  }

  editPublishView(publishTableData: PublishTableData[]) {
    this.editSinglePublishView(publishTableData[0])
  }

  async updateSinglePublishView({ publishInfo }: PublishTableData) {

    const data: ConfirmationDialogData = {
      confirmText: 'UPDATE NOW',
      title: 'Update Program Page',
      description: this.oneWillUpdateMessage
    }
    const confirmed = await this.openDialogGetResult(SimpleConfirmationDialogComponent, { width: '480px', disableClose: true, data })
    if (!confirmed) return
    const updated = await this.sharePublishService.updateMultiplePublishViews([publishInfo.publishCode])
    updated && this.setMessage(this.oneUpdatedMessage)

  }

  async updatePublishViews(publishTableData: PublishTableData[]) {

    const data: ConfirmationDialogData = {
      confirmText: 'UPDATE NOW',
      title: `Update Program Page${this.canUpdateCount === 1 ? '' : 's'}`,
      description: this.canUpdateCount === 1 ? this.oneWillUpdateMessage : `${this.canUpdateCount} pages will be updated immediately.`
    }
    const confirmed = await this.openDialogGetResult(SimpleConfirmationDialogComponent, { width: '480px', disableClose: true, data })
    if (!confirmed) return
    const updated = await this.sharePublishService.updateMultiplePublishViews(
      publishTableData
        .map(({ publishInfo }) => publishInfo)
        .filter(({ status }) => status === 'published')
        .map(({ publishCode }) => publishCode)
    )
    updated && this.setMessage(this.canUpdateCount === 1 ? this.oneUpdatedMessage : `${this.canUpdateCount} pages were updated successfully.`)

  }

  setMessage(message: string) {
    this.message = message
    setTimeout(() => this.message = '', this.MESSAGE_DISPLAY_MS)
  }

  duplicatePublishView(publishTableData: PublishTableData[]) {
    this.duplicateSinglePublishView(publishTableData[0])

  }

  duplicateSinglePublishView({ publishInfo }: PublishTableData) {
    this.openGreenInputDialog('Duplicate Page', `Duplicate ${publishInfo.title} to a new page`, 'Page name', 'DUPLICATE', `${publishInfo.title} copy`).then(result => result && this.sharePublishService.duplicatePublishView(this.eventService.eventId, publishInfo, result.values[0]))
  }

  deleteSinglePublishView(publishTableItem: PublishTableData, event) {
    this.deletePublishView([publishTableItem], event)
  }

  async deletePublishView(publishTableData: PublishTableData[], event) {
    const n = publishTableData.length
    const data: ConfirmDeleteData = {
      title: `Delete Page${n === 1 ? '' : 's'}`,
      rect: event.target.getBoundingClientRect(),
      para: `Are you sure you want to delete ${n === 1 ? 'this' : 'these ' + n} page${ n === 1 ? '' : 's'}?  This action cannot be undone`,
      deleteButtonText: 'DELETE',
      dialog: this.dialog
    }
    const del = await openConfirmDeleteGetResult(data)
    if (!del) return
    this.sharePublishService.deletePublishViews(publishTableData.map(data => data.publishInfo))
  }

  openGreenInputDialog(title: string, header: string, placeholder: string, successLabel: string, currentValue: string = null, emptyOK = false, secondValue: string = null, secondPlaceholder: string = null) {
    return this.openInputDialog(this.uiService.labelsByType.event.color, title, header, placeholder, successLabel, currentValue, emptyOK, secondValue, secondPlaceholder)
  }

  async addTimeline() {
    const result = await this.openGreenInputDialog('Create a Page', 'Give your new page a name and description', 'Page name', 'CREATE', null, false, '', 'Page description')
    if (!result) return
    const publishCode = await this.sharePublishService.addNewPublishView(result.values[0], result.values[1], this.publishList.length)
    if (!publishCode) return
    this.openPublishView(publishCode)
  }

  ngOnDestroy() { this.$selectTimeline.next(true); this.$selectTimeline.complete() }

}

